<template>
  <v-card
    elevation="0"
    class="overflow-hidden modern-volunteers-card rounded-3xl"
    :class="{ 'dark-mode': isDarkMode }"
  >
    <!-- Header Section with Gradient Background -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-icon">
          <v-icon size="32" class="icon-volunteers">mdi-account-group</v-icon>
        </div>
        <div class="header-text">
          <h1 class="header-title">Volunteers Overview</h1>
          <p class="header-subtitle">Real-time volunteer statistics and analytics</p>
        </div>
        <div class="header-actions">
          <v-btn
            icon
            size="small"
            variant="text"
            class="theme-toggle"
            @click="toggleTheme"
          >
            <v-icon>{{ isDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
          </v-btn>
        </div>
      </div>
    </div> 

    <!-- Chart Section -->
    <div class="chart-section">
      <div class="chart-container">
        <div class="chart-wrapper">
          <highcharts
            :options="chartOptions"
            class="modern-chart"
          ></highcharts>
        </div>
      </div>
    </div>

    <!-- Stats Section -->
    <div class="stats-section">
      <div class="stats-grid">
        <div
          class="stat-card stat-registered animate-in"
          @click="goToVolunteers"
          style="--delay: 0s"
        >
          <div class="stat-icon">
            <v-icon color="primary" size="24">mdi-account-multiple</v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-number">
              <span class="counter">{{ totalVolunteers || 0 }}</span>
            </div>
            <div class="stat-label">Total Volunteers</div>
          </div>
          <div class="stat-trend">
            <v-icon color="success" size="16">mdi-trending-up</v-icon>
            <!-- <span class="trend-value">7.5%</span> -->
          </div>
        </div>

        <div
          class="stat-card stat-pending animate-in"
          @click="goToActiveVolunteers"
          style="--delay: 0.1s"
        >
          <div class="stat-icon">
            <v-icon color="warning" size="24">mdi-clock-outline</v-icon>
          </div>
          <div class="stat-content">
            <div class="stat-number">
              <span class="counter">{{ totalHours || 0 }}</span>
            </div>
            <div class="stat-label">Total Hours</div>
          </div>
          <div class="stat-trend">
            <v-icon color="success" size="16">mdi-trending-up</v-icon>
            <!-- <span class="trend-value">12.3%</span> -->
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  data() {
    return {
      isDarkMode: false, // Default to light mode
      chartOptions: {
        chart: {
          type: "pie",
          backgroundColor: 'transparent',
          borderRadius: 16,
          style: {
            fontFamily: 'Inter, system-ui, sans-serif'
          }
        },
        credits: {
          enabled: false
        },
        title: {
          text: null
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          borderColor: 'transparent',
          borderRadius: 8,
          style: {
            color: '#ffffff',
            fontSize: '13px',
            fontWeight: '500'
          },
          pointFormat: '{point.name}: <b>{point.y} ({point.percentage:.1f}%)</b>'
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: "pointer",
            borderRadius: 5,
            borderWidth: 0,
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>',
              style: {
                color: '#ffffff',
                textOutline: '2px contrast',
                fontSize: '14px',
                fontWeight: '700'
              },
              distance: -30,
              connectorWidth: 0
            },
            showInLegend: true,
            colors: [
              '#A5D6A7', // Soft green
              '#81D4FA', // Light blue
              '#CE93D8', // Lavender
              '#FFF59D', // Pale yellow
              '#FFAB91', // Peach
              '#90CAF9', // Sky blue
              '#B39DDB'  // Soft purple
            ],
            events: {
              click: (event) => {
                this.goToVolunteersWithAge(event.point.name);
              }
            }
          }
        },
        series: [{
          name: "Age group",
          colorByPoint: true,
          data: this.volunteersGraphDatas
        }],
        legend: {
          align: 'right',
          verticalAlign: 'middle',
          layout: 'vertical',
          itemStyle: {
            color: '#64748b',
            fontWeight: '500'
          },
          itemHoverStyle: {
            color: '#1e293b'
          },
          symbolRadius: 0,
          symbolPadding: 8,
          itemMarginBottom: 8
        },
        responsive: {
          rules: [{
            condition: {
              maxWidth: 600
            },
            chartOptions: {
              legend: {
                align: 'center',
                verticalAlign: 'bottom',
                layout: 'horizontal',
                itemMarginBottom: 4,
                itemMarginTop: 0
              },
              plotOptions: {
                pie: {
                  dataLabels: {
                    enabled: false
                  },
                  size: '80%',
                  center: ['50%', '50%']
                }
              }
            }
          },
          {
            condition: {
              maxWidth: 400
            },
            chartOptions: {
              legend: {
                itemWidth: 120,
                itemStyle: {
                  fontSize: '11px'
                }
              },
              plotOptions: {
                pie: {
                  size: '70%'
                }
              }
            }
          }]
        }
      }
    };
  },
  computed: {
    ...mapGetters("adminDashboard", ["volunteersGraphData"]),
    volunteersGraphDatas() {
      return this.volunteersGraphData?.ageGroups || [];
    },
    totalVolunteers() {
      return this.volunteersGraphData?.totalVolunteers || 0;
    },
    totalHours() {
      return this.volunteersGraphData?.totalHours || 0;
    }
  },
  methods: {
    ...mapActions("adminDashboard", ["fetchVolunteersGraphDetails"]),
    goToVolunteers() {
      this.$router.push({ name: "admin-volunteers" });
    },
    goToActiveVolunteers() {
      this.$router.push({ 
        name: "admin-volunteers", 
        query: { activeVolunteers: 'yes' } 
      });
    },
    goToVolunteersWithAge(ageGroup) {
      this.$router.push({ name: "admin-volunteers", query: { age: ageGroup } });
    },
    toggleTheme() {
      this.isDarkMode = !this.isDarkMode;
      document.documentElement.setAttribute('data-theme', this.isDarkMode ? 'dark' : 'light');
      this.updateChartTheme();
    },
    updateChartTheme() {
      if (this.isDarkMode) {
        this.chartOptions.plotOptions.pie.dataLabels.style.color = '#ffffff';
        this.chartOptions.legend.itemStyle.color = '#94a3b8';
        this.chartOptions.legend.itemHoverStyle.color = '#f1f5f9';
      } else {
        this.chartOptions.plotOptions.pie.dataLabels.style.color = '#ffffff';
        this.chartOptions.legend.itemStyle.color = '#64748b';
        this.chartOptions.legend.itemHoverStyle.color = '#1e293b';
      }
    }
  },
  watch: {
    volunteersGraphData(newData) {
      this.chartOptions.series[0].data = newData?.ageGroups || [];
    },
    isDarkMode() {
      this.updateChartTheme();
    }
  },
  mounted() {
    this.fetchVolunteersGraphDetails();
    // Initialize with light mode
    document.documentElement.setAttribute('data-theme', 'light');
    this.updateChartTheme();
  }
};
</script>

<style scoped>
.modern-volunteers-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  width: 100%;
  margin: 0;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
}

.modern-volunteers-card.dark-mode {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2) !important;
}

/* Header Section */
.header-section {
  background: linear-gradient(135deg, #e91e62b2 0%, #9C27B0 50%, #3f51b5d9 100%);
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
}

.header-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.1)"/></svg>');
  background-size: 30px 30px;
  animation: float 20s infinite linear;
}

@keyframes float {
  0% { transform: translateY(0); }
  100% { transform: translateY(-30px); }
}

.header-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 1;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.75rem;
  border-radius: 1rem;
  backdrop-filter: blur(10px);
  flex-shrink: 0;
}

.icon-volunteers {
  color: white !important;
}

.header-text {
  flex: 1;
  min-width: 200px;
}

.header-title {
  color: white;
  font-size: clamp(1.25rem, 4vw, 1.5rem);
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.5px;
}

.header-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: clamp(0.75rem, 3vw, 0.875rem);
  margin: 0.25rem 0 0 0;
  line-height: 1.4;
  font-weight: 400;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}

.theme-toggle {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
  color: white !important;
  transition: all 0.3s ease;
}

.theme-toggle:hover {
  background: rgba(255, 255, 255, 0.3) !important;
  transform: rotate(15deg);
}

/* Chart Section */
.chart-section {
  padding: 1.5rem;
}

.chart-container {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 1.25rem;
  padding: 1.25rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.dark-mode .chart-container {
  background: rgba(15, 23, 42, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}

.chart-wrapper {
  min-height: 250px;
  height: 100%;
  width: 100%;
}

.modern-chart {
  height: 100%;
  width: 100%;
}

/* Stats Section */
.stats-section {
  padding: 0 1.5rem 1.5rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
  gap: 1rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.8);
  border-radius: 1.25rem;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(20px);
}

.stat-card.animate-in {
  animation: slideUp 0.6s ease forwards;
  animation-delay: var(--delay);
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dark-mode .stat-card {
  background: rgba(15, 23, 42, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--accent-color), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-card:hover::before {
  opacity: 1;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  background: rgba(112, 36, 196, 0.1);
  padding: 0.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 3rem;
  height: 3rem;
  flex-shrink: 0;
}

.dark-mode .stat-icon {
  background: rgba(167, 139, 250, 0.1);
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-number {
  font-size: clamp(1.25rem, 5vw, 1.75rem);
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.dark-mode .stat-number {
  color: #f1f5f9;
}

.stat-label {
  font-size: clamp(0.75rem, 3vw, 0.8125rem);
  color: #64748b;
  margin-top: 0.25rem;
  font-weight: 500;
}

.dark-mode .stat-label {
  color: #94a3b8;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #10b981;
  flex-shrink: 0;
}

.trend-value {
  color: inherit;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .header-content {
    gap: 0.75rem;
  }
  
  .header-text {
    min-width: 160px;
  }
  
  .chart-section {
    padding: 1rem;
  }
  
  .chart-container {
    padding: 1rem;
  }
  
  .stats-section {
    padding: 0 1rem 1rem;
  }
}

@media (max-width: 480px) {
  .header-section {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-actions {
    margin-left: 0;
    width: 100%;
    justify-content: flex-end;
  }
  
  .header-text {
    min-width: 100%;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .stat-card {
    padding: 1rem;
  }
}

/* Add these additional styles for better mobile responsiveness */
@media (max-width: 600px) {
  .chart-container {
    padding: 0.75rem;
  }
  
  .chart-wrapper {
    min-height: 220px;
  }
}

@media (max-width: 400px) {
  .chart-wrapper {
    min-height: 200px;
  margin: 0 -0.5rem;
  }
  
  .stats-grid {
    gap: 0.75rem;
  }
  
  .stat-card {
    padding: 1rem 0.75rem;
    gap: 0.75rem;
  }
  
  .stat-icon {
    min-width: 2.5rem;
    height: 2.5rem;
    padding: 0.5rem;
  }
}
</style>